import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api";
import { Camera, Trash2, Image, Download, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface ProgressPhoto {
  id: string;
  date: string;
  photo_type: "front" | "side" | "back";
  storage_path: string;
  url?: string;
}

const photoLabels: Record<string, string> = {
  front: "Frente",
  side: "Lado",
  back: "Costas",
};

const ProgressPhotos = () => {
  const { toast } = useToast();
  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedPhoto, setExpandedPhoto] = useState<ProgressPhoto | null>(null);

  const handleDownload = async (photo: ProgressPhoto) => {
    if (!photo.url) return;
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `progresso-${photo.date}-${photo.photo_type}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Erro ao baixar foto", variant: "destructive" });
    }
  };

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("api-photos", { action: "list" });
      setPhotos(res.data || []);
      setAccessDenied(false);
    } catch (e: any) {
      if (e.message?.includes("plano") || e.message?.includes("403")) {
        setAccessDenied(true);
      } else {
        console.error("Load photos error:", e);
      }
    }
    setLoading(false);
  };

  useEffect(() => { loadPhotos(); }, []);

  const handleDelete = async (photo: ProgressPhoto) => {
    setDeletingId(photo.id);
    try {
      await apiFetch("api-photos", { action: "delete", id: photo.id, path: photo.storage_path }, {
        method: "DELETE",
      });
      toast({ title: "Foto excluída com sucesso!" });
      loadPhotos();
    } catch (e: any) {
      toast({ title: "Erro ao deletar foto", description: e.message, variant: "destructive" });
    }
    setDeletingId(null);
  };

  if (accessDenied) {
    return (
      <div className="text-center py-8">
        <Camera className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Fotos de progresso disponíveis no plano PRO FIT PLUS ou superior</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="w-5 h-5 rounded-full" />
          <Skeleton className="h-5 w-40" />
        </div>
        <div className="bg-card rounded-xl p-4 border border-border space-y-3">
          <Skeleton className="h-4 w-48" />
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="w-full aspect-[3/4] rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const grouped = photos.reduce<Record<string, ProgressPhoto[]>>((acc, p) => {
    acc[p.date] = acc[p.date] || [];
    acc[p.date].push(p);
    return acc;
  }, {});

  const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          Fotos de Progresso
        </h3>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-8">
          <Image className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Nenhuma foto registrada</p>
          <p className="text-xs text-muted-foreground/70">Adicione fotos ao registrar suas medidas</p>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map(date => (
            <div key={date} className="bg-card rounded-xl p-4 border border-border">
              <button
                onClick={() => setSelectedDate(selectedDate === date ? null : date)}
                className="w-full text-left text-sm font-medium text-foreground mb-2"
              >
                {new Date(date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                <span className="text-xs text-muted-foreground ml-2">({grouped[date].length} foto{grouped[date].length > 1 ? "s" : ""})</span>
              </button>
              {(selectedDate === date || dates.indexOf(date) === 0) && (
                <div className="grid grid-cols-3 gap-2">
                  {grouped[date].map(photo => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={photo.url}
                        alt={photoLabels[photo.photo_type]}
                        className="w-full aspect-[3/4] object-cover rounded-lg cursor-pointer transition-opacity hover:opacity-90"
                        onClick={() => setExpandedPhoto(photo)}
                      />
                      <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                        {photoLabels[photo.photo_type]}
                      </span>
                      <button
                        onClick={() => handleDelete(photo)}
                        disabled={deletingId === photo.id}
                        className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100"
                      >
                        {deletingId === photo.id ? (
                          <span className="w-3 h-3 block border-2 border-destructive-foreground/40 border-t-destructive-foreground rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!expandedPhoto} onOpenChange={(open) => !open && setExpandedPhoto(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg p-0 bg-black/95 border-none overflow-hidden">
          {expandedPhoto && (
            <div className="relative flex flex-col items-center">
              <div className="absolute top-3 right-3 z-10 flex gap-2">
                <button
                  onClick={() => handleDownload(expandedPhoto)}
                  className="bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors backdrop-blur-sm"
                  title="Baixar foto"
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
              <img
                src={expandedPhoto.url}
                alt={photoLabels[expandedPhoto.photo_type]}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white text-sm font-medium">
                  {photoLabels[expandedPhoto.photo_type]} — {new Date(expandedPhoto.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgressPhotos;
